import { useCallback, useEffect, useRef, useState } from "react";

// Loads now and then every `intervalMs` while the tab is visible, so a driver's phone in a pocket does not
// keep asking. `reload` refreshes at once, for example right after a scan. A tick is skipped while a request
// is still out, and only the newest request's answer is kept, so a slow reply never overwrites a newer one.
export const usePolling = <T>(load: () => Promise<T>, intervalMs: number, deps: unknown[]) => {
  const [data, setData] = useState<T | null>(null);
  const [failed, setFailed] = useState(false);
  const loadRef = useRef(load);
  const latest = useRef(0);
  const inFlight = useRef(false);
  loadRef.current = load;

  const reload = useCallback(() => {
    const request = ++latest.current;
    inFlight.current = true;
    loadRef
      .current()
      .then((value) => {
        if (request !== latest.current) return;
        setData(value);
        setFailed(false);
      })
      .catch(() => request === latest.current && setFailed(true))
      .finally(() => {
        if (request === latest.current) inFlight.current = false;
      });
  }, []);

  useEffect(() => {
    setData(null);
    setFailed(false);
    reload();
    const tick = () => document.visibilityState === "visible" && !inFlight.current && reload();
    const timer = setInterval(tick, intervalMs);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [reload, intervalMs, ...deps]);

  return { data, failed, reload };
};

export default usePolling;
